import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  Renderer2,
  Signal,
  SimpleChange,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { combineLatest } from 'rxjs';

import { getRatingName } from '@recruitee/interview-results-util';
import {
  IconComponent,
  InterviewEvaluationRatingComponent,
  RtTooltip,
} from '@recruitee/user-interface';

@Component({
  selector: 'rt-evaluation-meter',
  templateUrl: './evaluation-meter.component.html',
  styleUrls: ['./evaluation-meter.component.less'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'state()',
    '[class.clickable]': 'isClickable()',
  },
  imports: [IconComponent, InterviewEvaluationRatingComponent],
  hostDirectives: [RtTooltip],
})
export class EvaluationMeterComponent {
  private readonly rtTooltip = inject(RtTooltip, { self: true });

  private readonly renderer = inject(Renderer2);

  public rating = input.required<number | null>();

  public tooltipEnabled = input<boolean, boolean | string>(false, { transform: booleanAttribute });

  public tooltipText = input<string | null>(null);

  public isDisqualified = input<boolean, boolean | string>(false, { transform: booleanAttribute });

  public isHidden = input<boolean, boolean | string>(false, { transform: booleanAttribute });

  public isClickable = input<boolean, boolean | string>(true, { transform: booleanAttribute });

  protected state = computed(() => {
    if (this.isDisqualified()) {
      return 'candidate-disqualified';
    } else if (this.isHidden()) {
      return 'rating-hidden';
    } else if (this.rating() === null) {
      return 'default';
    }

    return getRatingName(this.rating()!);
  });

  private pathGroup = viewChild.required<ElementRef>('pathGroup');

  private paths: Signal<SVGPathElement[]> = computed(
    () => <SVGPathElement[]>Array.from(this.pathGroup().nativeElement.children).reverse(),
  );

  private onColorChanges = combineLatest([
    toObservable(this.isHidden),
    toObservable(this.isDisqualified),
    toObservable(this.rating),
  ])
    .pipe(takeUntilDestroyed())
    .subscribe(() => {
      this.markToColorFromFirstToPrc(this.rating());
    });

  private onTooltipTextChanges = toObservable(this.tooltipText)
    .pipe(takeUntilDestroyed())
    .subscribe(() => {
      if (!this.tooltipText()?.length) {
        return;
      }

      this.rtTooltip.message = this.tooltipText();
      this.rtTooltip.ngOnChanges({
        message: new SimpleChange(this.rtTooltip.message, this.tooltipText(), false),
      });
    });

  private markToColorFromFirstToPrc(prcOfPathsToColor: number | null): void {
    /**
     * Ex.: ceil(33 / 100 * 25) = ceil(0.33 * 25) = ceil(8.25) = 9.
     * But, we always want to color at least one path, hence Math.max.
     * We subtract 1, because the index is zero-based.
     * We don't worry about values < 0 and values > 100.
     * For prcOfPathsToColor = null, we don't color any path.
     */
    const lastColoredPathIndex =
      prcOfPathsToColor === null
        ? -1
        : Math.max(1, Math.ceil((prcOfPathsToColor / 100) * this.paths().length)) - 1;

    this.paths().forEach((path, index) => {
      if (index <= lastColoredPathIndex) {
        this.renderer.addClass(path, 'colored');
      } else {
        this.renderer.removeClass(path, 'colored');
      }
    });
  }
}
